import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Screen } from '@aibify/display/client';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/auth.ts';
import { T } from '../lib/strings.ts';
import { fmtWhen } from '../lib/time.ts';
import { ErrorView, Loading } from '../components/Status.tsx';
import { Badge, StatusLabel, runTone } from '../components/ui/badge.tsx';
import { IconArrowBack, IconClock } from '../components/brand/icons.tsx';
import { MailConnectCard } from '../components/MailConnectDrawer.tsx';
import { ScreenExpand } from '../components/ScreenExpand.tsx';
import { RestrictedNote } from '../components/DataAccess.tsx';

export function RunPage() {
  const { slug = '', runId = '' } = useParams();
  const s = useSession();
  const q = useQuery({ queryKey: ['run', runId], queryFn: () => api.getRun(runId) });
  if (q.isPending) return <Loading variant="screen" />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const r = q.data;
  return (
    <section className="flex flex-col gap-4">
      <Link to={`/artifacts/${slug}`} className="-mb-2 inline-flex min-h-11 w-fit items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary">
        <IconArrowBack size={16} />
        {T.artifact.back}
      </Link>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border bg-card px-4 py-3 text-sm text-muted-foreground shadow-card">
        <StatusLabel tone={runTone(r.status)}>
          {T.artifact.status[r.status] ?? r.status}
        </StatusLabel>
        {r.startedAt && (
          <bdi dir="auto" className="font-medium text-foreground tabular-nums">
            {fmtWhen(r.startedAt)}
          </bdi>
        )}
        <span>{T.artifact.runBy(r.user.nameHe)}</span>
        {r.trigger && (
          <Badge tone="info" className="gap-1" data-testid="run-trigger">
            <IconClock size={14} />
            {T.triggers.firedBy(r.trigger.nameHe)}
            {r.trigger.attempt === 2 && <span data-testid="run-second-attempt"> · {T.triggers.secondAttempt}</span>}
          </Badge>
        )}
        <span>{T.artifact.version(r.version)}</span>
      </div>
      {r.errorHe && <ErrorView error={{ textHe: r.errorHe }} />}
      {/* 20: נעצרה כי אין Gmail — חיבור מכאן, ואז "הרץ שוב" ביצירה */}
      {r.mailConnect && <MailConnectCard caps={r.mailConnect.caps} />}
      {r.output != null && <RestrictedNote />}
      {r.output != null && (
        <ScreenExpand>
  <Screen screen={r.output} asOf={r.asOf} runAsHe={r.runAs.id !== r.user.id ? r.runAs.nameHe : null} fetchFile={api.fetchFile} exportedBy={s?.user.nameHe} />
        </ScreenExpand>
      )}
      {r.log && (
        <details className="rounded-md border border-border bg-card px-4 py-1 shadow-card">
          <summary className="flex min-h-11 cursor-pointer items-center font-semibold">{T.artifact.log}</summary>
          <pre dir="ltr" className="m-0 mb-3 overflow-x-auto rounded-md bg-muted p-3 text-start font-mono text-[13px] whitespace-pre-wrap">{r.log}</pre>
        </details>
      )}
    </section>
  );
}
